import React, { useState, useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import UserAssetList from './UserAssetList';
import UserAssetModal from './UserAssetModal';
import { useAuth0 } from '@auth0/auth0-react';
import '../../styles/components/UserInventory.css';
import { FEATURE_FLAGS, isFeatureEnabled } from '../../config/featureFlags';

export const UserAssetPage = () => {
    const bitcoinEnabled = isFeatureEnabled(FEATURE_FLAGS.bitcoinExperience);
    const barterEnabled = isFeatureEnabled(FEATURE_FLAGS.marketplaceBarter);
    const sheafEnabled = isFeatureEnabled(FEATURE_FLAGS.sheafPurchase);
    const { userId } = useParams();
    const location = useLocation();
    const [assets, setAssets] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [handledListingIntent, setHandledListingIntent] = useState('');
    const { getAccessTokenSilently } = useAuth0();
    const apiUrl = process.env.REACT_APP_API_URL;

    useEffect(() => {
        document.title = 'Inventory | ThousandYearKingdom.org';
    }, []);

    const fetchAssets = async () => {
        try {
            const token = await getAccessTokenSilently({
                authorizationParams: {
                    audience: process.env.REACT_APP_API_AUDIENCE,
                }
            });

            const response = await fetch(`${apiUrl}/api/assets/user/${userId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            const data = await response.json();
            setAssets(data);
        } catch (error) {
            console.error('Error fetching assets:', error);
        }
    };

    useEffect(() => {
        fetchAssets();
    }, [userId, showAddModal]);

    useEffect(() => {
        const listingAction = new URLSearchParams(location.search).get('action');
        if (listingAction === 'list-new' && handledListingIntent !== location.search) {
            setShowAddModal(true);
            setHandledListingIntent(location.search);
        }
    }, [handledListingIntent, location.search]);

    useEffect(() => {
        const listingAssetId = new URLSearchParams(location.search).get('listAssetId');
        if (!listingAssetId || assets.length === 0) {
            return;
        }

        const assetToList = assets.find((asset) => String(asset.id) === listingAssetId);
        if (assetToList && String(selectedAsset?.id) !== listingAssetId) {
            setSelectedAsset(assetToList);
        }
    }, [assets, location.search, selectedAsset?.id]);

    const handleAddAssetClick = () => {
        setShowAddModal(true);
    };

    const handleAssetCardClick = (asset) => {
        setSelectedAsset(asset);
    };
    const handleListAssetClick = (asset) => {
        setSelectedAsset(asset);
    };
    return (
        <div className="user-inventory-page">
            <header className="user-inventory-header">
                <div className="user-inventory-heading-copy">
                    <span className="user-inventory-kicker">Your goods</span>
                    <h1 className="user-inventory-title">Inventory</h1>
                    <p>
                        Choose an item to make it available for {[barterEnabled && 'direct trade', bitcoinEnabled && 'BTC', sheafEnabled && 'Sheaf'].filter(Boolean).join(', ') || 'the community marketplace'}.
                    </p>
                    <div className="user-inventory-header-actions">
                        <Button
                            className="user-inventory-list-new-button"
                            onClick={handleAddAssetClick}
                        >
                            Give away or trade an item
                        </Button>
                        <Link to="/marketplace" className="user-inventory-marketplace-link">
                            Browse marketplace
                        </Link>
                    </div>
                </div>
                <Button
                    className="user-inventory-add-button"
                    onClick={handleAddAssetClick}
                    aria-label="Add inventory item"
                    title="Give away or trade an item"
                >
                    <span className="user-inventory-add-plus" aria-hidden="true">+</span>
                </Button>
            </header>

            <UserAssetList
                assets={assets}
                onAssetCardClick={handleAssetCardClick}
                onListAssetClick={handleListAssetClick}
            />

            <UserAssetModal
                show={showAddModal}
                onHide={() => setShowAddModal(false)}
                asset={null}
                onAssetAdded={fetchAssets}
                listingIntent={new URLSearchParams(location.search).get('action') === 'list-new'}
            />

            <UserAssetModal
                show={!!selectedAsset}
                onHide={() => setSelectedAsset(null)}
                asset={selectedAsset}
                onAssetAdded={fetchAssets}
            />
        </div>
    );
};
